<template>
  <water-balance-reports-main-dialog
    :is-visible.sync="computedIsVisible"
    :is-fetched="isFetched"
    :is-chart-ready="isChartReady"
    :dialog-title="titleItem"
    :table-data="tableData"
    ref="waterBalanceMainDialog"
  >
    <template #prepend-table>
      <v-row v-if="report" no-gutters>
        <v-col class="text-h6 text-left px-5 pt-6">
          {{ report.name }}
        </v-col>
      </v-row>
    </template>
    <template #chart>
      <v-row no-gutters class="pt-7 pl-15 align-center" style="gap: 2rem">
        <v-col cols="3">
          <data-input v-bind="reportInput" v-model="report" @change="onReportChange" />
        </v-col>
        <v-col cols="3">
          <data-input v-if="isDateInputVisible" v-bind="dateInput" v-model="date" @saveDate="onDateChange" />
        </v-col>
        <v-col cols="3" class="ml-5">
          <base-button-subheader
            translation-path="dialog.waterBalanceReports.details"
            :disabled="!isFetched"
            @click="openDetailsDialog"
          />
        </v-col>
      </v-row>
      <v-row v-if="isFetching" class="pt-5">
        <v-col>
          <main-loader />
        </v-col>
      </v-row>
      <template v-else>
        <v-row no-gutters class="pt-7 pb-3 pl-15">
          <v-col v-if="isFetched" cols="3">
            <data-input v-bind="parameterInput" v-model="parameter" @change="onParamChange" />
          </v-col>
        </v-row>
        <v-row no-gutters class="justify-center align-center">
          <v-col cols="10">
            <canvas id="chart" role="img" height="450px"></canvas>
          </v-col>
        </v-row>
      </template>
      <water-balance-reports-details-dialog
        v-if="isDetailsDialogLoaded"
        :is-visible.sync="isDetailsDialogVisible"
        :data="mappedValues"
        :parameters-list="parametersList"
        :parameter-input-props="parameterInput"
        :date-input-props="dateInput"
        :current-report="report"
      ></water-balance-reports-details-dialog>
    </template>
  </water-balance-reports-main-dialog>
</template>

<script>
import { call, get } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import WaterBalanceReportsDetailsDialog from './WaterBalanceReportsDetailsDialog.vue';
import WaterBalanceReportsMainDialog from './WaterBalanceReportsMainDialog.vue';

export default {
  name: 'WaterBalanceReportsDialog',
  components: {
    DataInput,
    MainLoader,
    WaterBalanceReportsDetailsDialog,
    WaterBalanceReportsMainDialog,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    isDetailsDialogVisible: false,
    isDetailsDialogLoaded: false,
    parameter: null,
    date: null,
    isFetched: false,
    isChartReady: false,
    report: null,
    mappedValues: null,
    tableItems: null,
    tableHeaders: null,
    isFetching: false,
    isDateInputVisible: true,
  }),
  watch: {
    report() {
      this.clearData();
    },
    isVisible(nV) {
      if (!nV) return;
      this.clearData();
      this.report = null;
    },
  },
  computed: {
    waterBalanceResultsDatasource: get('admin/modulesMapping@water_balance.results_datasource'),
    waterBalanceReportsDatasourceMetadata: get('layers/metadata@:waterBalanceResultsDatasource'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    parameters() {
      const balanceParameters = {
        inflow_volume: 'mSquare',
        sold_volume: 'mSquare',
        domestic_sold_volume: 'mSquare',
        self_usage_volume: 'mSquare',
        loss_volume: 'mSquare',
        alarm_avg_inflow: null,
        alarm_max_inflow: null,
      };
      const allowedParameters = {
        balance: balanceParameters,
        iwa: {
          ...balanceParameters,
          population_per_km: 'peoplePerNetwork',
          terminals_density: 'terminalsPerNetwork',
          inflow_unit_volume: 'mSquarePerCapita',
          sold_unit_volume: 'mSquarePerCapita',
          domestic_sold_unit_volume: 'mSquarePerCapita',
          not_sold_volume: 'mSquarePerCapita',
          loss_unit_volume: 'mSquarePerNetwork',
          loss_unit_volume_per_capita: 'mSquarePerCapita',
          loss_unit_volume_per_terminal: 'mSquarePerTerminal',
          damage_intensity: 'onePerNetwork',
          uarl: 'mSquare',
          ili: null,
          rlb: 'mSquarePerNetworkOrTerminal',
        },
      };
      return allowedParameters;
    },
    parametersList() {
      if (!this.report) return {};
      const currentParameters = this.parameters[this.report?.type];
      const attrs = this.waterBalanceReportsDatasourceMetadata?.attributes_schema.attributes
        .filter(attr => Object.keys(currentParameters).includes(attr.name))
        .map(attr => [
          attr.name,
          {
            name: attr.verbose_name,
            label: currentParameters[attr.name]
              ? this.$i18n.t(`dialog.waterBalanceReports.units.${currentParameters[attr.name]}`)
              : '',
          },
        ]);
      return Object.fromEntries(attrs) || {};
    },
    parameterInput() {
      return {
        dataType: {
          name: 'select',
        },
        itemValue: 'value',
        itemText: 'name',
        hideDetails: true,
        label: this.$i18n.t('dialog.waterBalanceReports.parameter'),
        items: Object.entries(this.parametersList).map(([value, data]) => {
          return { value, name: data.name };
        }),
      };
    },
    reportInput() {
      return {
        dataType: {
          name: 'select',
        },
        returnObject: true,
        itemText: 'name',
        hideDetails: true,
        label: this.$i18n.t('dialog.waterBalanceReports.report'),
        items: [
          {
            value: 'Bilansowanie',
            type: 'balance',
            dateType: 'date',
            dateFormat: 'YYYY-MM-DD',
            name: this.$i18n.t('dialog.waterBalanceReports.waterBalanceBalance'),
          },
          {
            value: 'Statystyki IWA miesięczne',
            type: 'iwa',
            dateType: 'month',
            dateFormat: 'YYYY-MM',
            name: this.$i18n.t('dialog.waterBalanceReports.waterBalanceIwaMonth'),
          },
          {
            value: 'Statystyki IWA roczne',
            type: 'iwa',
            dateType: 'year',
            dateFormat: 'YYYY',
            name: this.$i18n.t('dialog.waterBalanceReports.waterBalanceIwaYear'),
          },
        ],
      };
    },
    dateInput() {
      return {
        dataType: {
          name: 'date',
        },
        disabled: !this.report,
        datePickerProps: {
          type: this.report?.dateType,
        },
        localeDateFormat: this.report?.dateFormat,
        hideDetails: true,
        prependInnerIcon: 'mdi-calendar',
        label: this.$i18n.t('dialog.waterBalanceReports.date'),
      };
    },
    titleItem() {
      return this.$i18n.t('dialog.waterBalanceReports.title');
    },
    tableSummary() {
      return this.tableHeaders?.map(header => {
        if (header.value === 'dma_id') return `${this.$i18n.t('dialog.waterBalanceReports.tableSummary')}:`;
        if (['dma_name'].includes(header.value)) return '';
        else {
          return parseFloat(
            this.tableItems.reduce((a, b) => {
              if (!b[header.value]) b[header.value] = 0;
              return a + b[header.value];
            }, 0)
          ).toFixed(2);
        }
      });
    },
    tableData() {
      return {
        headers: this.tableHeaders,
        items: this.tableItems,
        summary: this.tableSummary,
        fileName: this.report?.name,
      };
    },
  },
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    onDateChange() {
      this.getData();
    },
    onReportChange() {
      if (this.isChartReady) {
        this.$refs['waterBalanceMainDialog'].chart.destroy();
        this.isChartReady = false;
      }
    },
    onParamChange() {
      this.drawChart();
      this.setTableData();
    },
    clearData() {
      this.isFetched = false;
      this.parameter = null;
      if (this.isChartReady) this.$refs['waterBalanceMainDialog'].chart.destroy();
      this.isChartReady = false;
      this.isDateInputVisible = false;
      this.$nextTick(() => {
        this.date = null;
        this.isDateInputVisible = true;
      });
    },
    setTableData() {
      const defaultProps = {
        align: 'center',
        ...(this.report.type === 'iwa' && { width: '150px' }),
      };
      const itemHeaders = Object.entries(this.parametersList).map(([key, value]) => {
        return {
          text: value.name,
          value: key,
          ...defaultProps,
        };
      });
      this.tableHeaders = [
        {
          text: this.$i18n.t('dialog.waterBalanceReports.id'),
          value: 'dma_id',
          ...defaultProps,
        },
        {
          text: this.$i18n.t('dialog.waterBalanceReports.name'),
          value: 'dma_name',
          ...defaultProps,
        },
        ...itemHeaders,
      ];
      this.tableItems = Object.values(this.mappedValues).map(value => value.itemData);
    },
    openDetailsDialog() {
      this.isDetailsDialogLoaded = true;
      this.isDetailsDialogVisible = true;
    },
    getChartData() {
      let labels = [];
      let data = [];
      Object.values(this.mappedValues).forEach(ft => {
        labels.push(ft.itemData.dma_id);
        data.push(ft.itemData[this.parameter]);
      });
      return {
        datasets: [
          {
            fill: false,
            lineTension: 1,
            borderColor: '#2a579a',
            backgroundColor: '#2a579a',
            data,
          },
        ],
        labels,
      };
    },
    async getData() {
      this.isFetched = false;
      this.isFetching = true;
      try {
        const startDate = this.$dayjs(this.date).format('YYYY-MM-DD');
        const endDate = this.$dayjs(this.date)
          .endOf(this.report.dateType === 'date' ? 'd' : this.report.dateType === 'month' ? 'M' : 'y')
          .format('YYYY-MM-DDTHH:mm:ss');
        const r = await this.getDataSourceFeatures({
          dataSource: this.waterBalanceResultsDatasource,
          filters: {
            '!AND': [
              {
                $EQUAL: {
                  [`${this.waterBalanceResultsDatasource}.analysis_type`]: {
                    value: this.report.value,
                  },
                },
              },
              {
                $GREATER_OR_EQUAL: {
                  [`${this.waterBalanceResultsDatasource}.analysis_datetime`]: {
                    value: startDate,
                  },
                },
              },
              {
                $LESS_OR_EQUAL: {
                  [`${this.waterBalanceResultsDatasource}.analysis_datetime`]: {
                    value: endDate,
                  },
                },
              },
            ],
          },
        });
        const featuresArray = r.features.map(ft => ft.properties);
        this.mappedValues = featuresArray.reduce((acc, obj) => {
          const objProps = obj;
          let key = objProps.dma_id;
          if (!acc[key]) {
            acc[key] = {
              label: objProps.dma_name,
              dma_id: objProps.dma_id,
              borderWidth: 1,
              fill: false,
              tension: 0,
              itemData: objProps,
            };
          }
          return acc;
        }, {});
        this.setTableData();
        this.isFetching = false;
        this.isFetched = true;
      } catch (e) {
        console.error('error:', e);
      }
    },
    getXAxis() {
      return {
        id: 'bottom',
        position: 'bottom',
        ticks: {
          callback: value => {
            return this.mappedValues[value].itemData.dma_name;
          },
        },
      };
    },
    async drawChart() {
      await this.$nextTick();
      if (this.isChartReady) {
        this.$refs['waterBalanceMainDialog'].chart.destroy();
      }
      this.isChartReady = false;
      const canvas = document.getElementById('chart');
      const chartOptions = {
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false,
          },
          elements: {
            point: {
              radius: 0,
            },
          },
          scales: {
            yAxes: [
              {
                id: 'leftAxis',
                type: 'linear',
                position: 'left',
                scaleLabel: {
                  display: true,
                  labelString: this.parametersList[this.parameter].label,
                },
              },
            ],
            xAxes: [this.getXAxis()],
          },
        },
        type: 'bar',
        data: this.getChartData(),
      };
      this.$refs['waterBalanceMainDialog'].generateChart(canvas, chartOptions);
      this.isChartReady = true;
    },
  },
};
</script>

<style lang="scss" scoped></style>
